import React from 'react';
import PropTypes from 'prop-types';
import '../styles/metric.css';
import { translate } from 'react-polyglot';

const M = 'M';
const A = 'A';
const L = 'L';
const rad = a => Math.PI * a / 180;
const rx = (c, r, a) => c + r * Math.cos(rad(a));
const ry = (c, r, a) => c + r * Math.sin(rad(a));

const createPath = (size, value, strokeWidth) => {
  const c = Math.abs(size) / 2; // Center
  const r1 = c; // Outer radius
  const r2 = c - Math.abs(strokeWidth); // Inner radius
  const angle = Math.abs(value % 1) * 360 - 90;

  const largeArc = value > 0.5 ? 1 : 0;

  const arc1 =
    value === 1
      ? [A, r1, r1, 0, 0, 1, c, c + r1, A, r1, r1, 0, 0, 1, c, c - r1]
      : [A, r1, r1, 0, largeArc, 1, rx(c, r1, angle), ry(c, r1, angle)];

  const arc2 =
    value === 1
      ? [A, r2, r2, 0, 0, 0, c, c + r2, A, r2, r2, 0, 0, 0, c, c - r2]
      : [A, r2, r2, 0, largeArc, 0, c, c - r2];

  return [
    M,
    c,
    c - r1,
    ...arc1,
    L,
    rx(c, r2, angle),
    ry(c, r2, angle),
    ...arc2
  ].join(' ');
};

const createBg = (size, strokeWidth) => {
  const c = Math.abs(size) / 2; // Center
  const r1 = c; // Outer radius
  const r2 = c - Math.abs(strokeWidth); // Inner radius

  return [
    M,
    c,
    0,
    A,
    r1,
    r1,
    0,
    0,
    1,
    c,
    size,
    A,
    r1,
    r1,
    0,
    0,
    1,
    c,
    0,
    M,
    c,
    c - r2,
    A,
    r2,
    r2,
    0,
    0,
    0,
    c,
    c + r2,
    A,
    r2,
    r2,
    0,
    0,
    0,
    c,
    c - r2
  ].join(' ');
};

/**
 * A single-value donut chart with percentage
 */

const Donut = ({ value, size, strokeWidth, children, t, ...props }) => {
  const viewBox = `0 0 ${size} ${size}`;

  const sx = {
    root: {
      position: 'relative',
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size / 4,
      lineHeight: 1,
      width: size,
      height: size,
      fontFamily: 'Lato, sans-serif'
    },
    svg: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      fill: props.color
    },
    bg: {
      fill: '#F8F8F7'
    },
    percentage: {
      fontSize: '2.5rem',
      color: '#3c4f66',
      marginBottom: '.5rem',
      fontWeight: '400'
    },
    satisfaction: {
      fontSize: '14px',
      letterSpacing: '.4px',
      color: '#3c4f66',
      lineHeight: '1.14',
      width: '132px',
      fontWeight: '300'
    },
    notEnoughData: {
      fontSize: '18px',
      color: '#D8DCE0',
      textTransform: 'uppercase',
      width: '120px',
      fontWeight: 'bold',
      lineHeight: '1.33'
    }
  };

  return (
    <div {...props} style={sx.root}>
      <svg viewBox={viewBox} width={size} height={size} style={sx.svg}>
        <path d={createBg(size, strokeWidth)} style={sx.bg} />
        <path d={createPath(size, value, strokeWidth)} />
      </svg>
      {value > 0
        ? <span>
            <div className="metric__number">
              {Math.round(value * 100)}%
            </div>
            <div className="metric__description">
              {children}
            </div>
          </span>
        : <span style={sx.notEnoughData}>
            {t('quality_not_enough_data')}
          </span>}
    </div>
  );
};

Donut.propTypes = {
  value: PropTypes.number,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  color: PropTypes.string,
  notEnoughData: PropTypes.string
};

Donut.defaultProps = {
  value: 100,
  size: 180,
  strokeWidth: 8,
  color: '#5172fc'
};

export default translate()(Donut);
